import { useCallback, useMemo } from 'react'
import { recordEmojiUse } from './frequentEmoji'
import type { ReactionDocument } from '@/lib/db/rxdb/hooks/entities/reactions'
import { useReactionMutations, useReactions } from '@/lib/db/rxdb/hooks'
import { useMember } from '@/lib/hooks/useWorkspaceUsers'
import useLoginStore from '@/store/LoginStore'

export interface ReactionGroup {
  emoji: string
  count: number
  userIds: Array<string>
  /** The current user is one of the reactors: the pill takes the terracotta tint. */
  mine: boolean
}

/**
 * Reactions on one entity (a comment, a task), grouped by emoji in the order
 * each emoji was first used, plus a toggle that adds or removes yours.
 */
export function useEntityReactions(entityType: string, entityId: string, envId?: string) {
  const currentUserId = useLoginStore((state) => state.userId)
  const { member: me } = useMember(currentUserId)
  const { reactions: all } = useReactions({ entityType, entityId, isDeleted: false })
  const { createReaction, deleteReaction } = useReactionMutations()

  const reactions = useMemo(
    () =>
      all
        .filter(
          (r: ReactionDocument) =>
            r.entityType === entityType &&
            r.entityId === entityId &&
            !r.isDeleted &&
            (!envId || r.envId === envId),
        )
        .sort((a, b) => a.createdOn - b.createdOn),
    [all, entityType, entityId, envId],
  )

  const groups = useMemo(() => {
    const byEmoji = new Map<string, ReactionGroup>()
    for (const r of reactions) {
      const group = byEmoji.get(r.emoji) ?? { emoji: r.emoji, count: 0, userIds: [], mine: false }
      group.count++
      group.userIds.push(r.userId)
      if (r.userId === currentUserId) group.mine = true
      byEmoji.set(r.emoji, group)
    }
    return [...byEmoji.values()]
  }, [reactions, currentUserId])

  const toggle = useCallback(
    async (emoji: string, label?: string) => {
      if (!currentUserId) return
      const mine = reactions.find((r) => r.emoji === emoji && r.userId === currentUserId)
      if (mine) {
        await deleteReaction(mine.id)
        return
      }
      recordEmojiUse(emoji)
      await createReaction({
        entityType,
        entityId,
        emoji,
        emojiLabel: label ?? null,
        userId: currentUserId,
        userName: me?.name || me?.email || null,
        userEmail: me?.email || null,
      })
    },
    [currentUserId, reactions, deleteReaction, createReaction, entityType, entityId, me],
  )

  return { groups, toggle, currentUserId }
}
